import { generateFieldColors, generateGridColors, randomCrossField, fieldHex } from './colors.js';
import { random } from './rng.js';

/** One line of the puzzle, kept around so a board can be inspected/dumped. */
export interface LineInfo {
  dir: 'H' | 'V';
  cells: number[]; // flat positions along the line, in grid order
}

export interface CrosswordData {
  gridW: number;
  gridH: number;
  activeCells: number[];
  colors: number[];
  anchors: Set<number>;
  lines: LineInfo[];
  /** Cells that start already in place and can't be moved — the filled-in
   *  middle of an edges board. Immovable like an anchor, but drawn set into
   *  the board rather than ringed: a whole interior of rings is just noise. */
  preset?: Set<number>;
}

/** What shape of board a game is played on. */
export type BoardMode = 'grid' | 'cross' | 'edges' | 'field';

// ── Full-grid wrappers ────────────────────────────────────────────────────────

/** Grid sizes, [cols, rows], for S / M / L / XL. */
export const GRID_SIZES: [number, number][] = [
  [4, 3],
  [6, 4],
  [6, 5],
  [8, 6],
];

/**
 * Edges boards can afford to be bigger than full grids at the same step of the
 * ladder: only the frame is ever in play, so an 8x7 asks for 26 tiles rather
 * than 56.
 */
export const EDGE_SIZES: [number, number][] = [
  [5, 4],
  [6, 5],
  [7, 6],
  [8, 7],
];

/** Build the board a (mode, size, difficulty) triple asks for. */
export function makeBoard(
  mode: BoardMode, sizeIndex: number, diffIndex: number, vivid: boolean,
): CrosswordData {
  if (mode === 'cross') return generateCrossword(diffIndex, vivid, sizeIndex);
  const [cols, rows] = (mode === 'edges' ? EDGE_SIZES : GRID_SIZES)[sizeIndex];
  if (mode === 'edges') return generateEdgeGrid(diffIndex, rows, cols, vivid);
  if (mode === 'field') return generateFieldGrid(diffIndex, rows, cols);
  return generateFullGrid(diffIndex, rows, cols, vivid);
}


function fullGridOf(colors: number[], rows: number, cols: number): CrosswordData {
  const total       = rows * cols;
  const activeCells = Array.from({ length: total }, (_, i) => i);
  const anchors     = new Set<number>([0, total - 1]);
  const lines: LineInfo[] = Array.from({ length: rows }, (_, r) => ({
    dir: 'H' as const,
    cells: Array.from({ length: cols }, (_, c) => r * cols + c),
  }));
  return { gridW: cols, gridH: rows, activeCells, colors, anchors, lines };
}

export function generateFullGrid(
  diffIndex: number, rows: number, cols: number, vivid: boolean
): CrosswordData {
  return fullGridOf(generateGridColors(diffIndex, rows, cols, vivid), rows, cols);
}

/** Same board as a full grid, coloured from the two-axis chromatic field. */
export function generateFieldGrid(
  diffIndex: number, rows: number, cols: number
): CrosswordData {
  return fullGridOf(generateFieldColors(diffIndex, rows, cols), rows, cols);
}

/**
 * The same gradient, but with only the frame in play — every interior cell
 * starts filled in at its right place.
 *
 * A full grid asks the player to read a two-dimensional gradient with nothing
 * but two corners to go on, which is a lot to take in cold. Here the middle of
 * the picture is already there to read off, and the job is to fit the border
 * back around it, so the same colour sense is being taught over far fewer
 * tiles.
 */
export function generateEdgeGrid(
  diffIndex: number, rows: number, cols: number, vivid: boolean
): CrosswordData {
  const grid   = generateFullGrid(diffIndex, rows, cols, vivid);
  const preset = new Set<number>();
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) preset.add(r * cols + c);
  }
  return { ...grid, preset };
}

// ── Runs ──────────────────────────────────────────────────────────────────────

/** Start/length of each maximal stretch of occupied cells along one row or column. */
function runsAlong(
  length: number, at: (i: number) => number, occ: Set<number>,
): { start: number; len: number }[] {
  const out: { start: number; len: number }[] = [];
  let i = 0;
  while (i < length) {
    if (!occ.has(at(i))) { i++; continue; }
    let j = i;
    while (j < length && occ.has(at(j))) j++;
    out.push({ start: i, len: j - i });
    i = j;
  }
  return out;
}

/**
 * Every maximal straight run of 3+ cells — exactly what reads as "one line" on
 * screen. Runs of 2 are excluded by the shape rules, so every cell lands in one.
 */
export function extractRuns(occ: Set<number>, gW: number, gH: number): LineInfo[] {
  const lines: LineInfo[] = [];

  for (let r = 0; r < gH; r++) {
    for (const run of runsAlong(gW, c => r * gW + c, occ)) {
      if (run.len >= 3) {
        lines.push({ dir: 'H', cells: Array.from({ length: run.len }, (_, k) => r * gW + run.start + k) });
      }
    }
  }
  for (let c = 0; c < gW; c++) {
    for (const run of runsAlong(gH, r => r * gW + c, occ)) {
      if (run.len >= 3) {
        lines.push({ dir: 'V', cells: Array.from({ length: run.len }, (_, k) => (run.start + k) * gW + c) });
      }
    }
  }
  return lines;
}

// ── Shape ─────────────────────────────────────────────────────────────────────
//
// Hue comes from the column, so two tiles share a hue exactly when they share a
// column. For lines to share a hue only where they genuinely cross, it's enough
// that **every column holds one unbroken stretch of cells**: then all the tiles
// of a given hue sit in a single straight line rather than being scattered
// across the board.
//
// That one rule is the whole constraint, and it leaves a lot of room — spines
// with arms, staircases, zigzags, forks. A column is therefore described by the
// stretch it holds, and a board is a list of those.

/** The unbroken stretch of cells a column holds, as inclusive row bounds. */
export interface Span { lo: number; hi: number }

/**
 * A stretch is 1 cell or 3+, never 2 — two cells are too few to read as a
 * gradient. Single cells are common, so boards get long arms and not just
 * combs.
 */
function pickLength(room: number, gH: number): number {
  // Long enough to fill a big board, but never the full height — a stretch that
  // reached both edges would leave its neighbours nowhere to join it.
  const max = Math.min(room, Math.max(6, gH - 1));
  if (max < 3 || random() < 0.3) return 1;
  return 3 + Math.floor(random() * (max - 2));
}

/**
 * The stretch for the next column along. It has to meet the previous column at
 * exactly one cell: fewer and the board falls apart, more and the two columns
 * form a 2x2 block.
 */
function nextSpan(prev: Span, gH: number): Span {
  const joint = prev.lo + Math.floor(random() * (prev.hi - prev.lo + 1));

  // Growing away from an end of the previous stretch keeps the overlap at one
  // cell. Joining in the middle of it leaves room for a single cell only.
  const dirs: number[] = [];
  if (joint === prev.hi) dirs.push(1);
  if (joint === prev.lo) dirs.push(-1);
  if (!dirs.length) return { lo: joint, hi: joint };

  const dir = dirs[Math.floor(random() * dirs.length)];
  const len = pickLength(dir === 1 ? gH - joint : joint + 1, gH);
  return dir === 1
    ? { lo: joint, hi: joint + len - 1 }
    : { lo: joint - len + 1, hi: joint };
}

/** Rows of a run of exactly 2 read no better than columns of one. */
function hasFlatPair(spans: (Span | null)[], gW: number, gH: number): boolean {
  for (let r = 0; r < gH; r++) {
    let run = 0;
    for (let c = 0; c <= gW; c++) {
      const s = c < gW ? spans[c] : null;
      if (s && r >= s.lo && r <= s.hi) run++;
      else { if (run === 2) return true; run = 0; }
    }
  }
  return false;
}

/** Lay out a board as one stretch per column, over a random run of columns. */
export function buildShape(gW: number, gH: number): (Span | null)[] | null {
  // Mostly the full width, sometimes a column or two narrower.
  const width = gW - Math.floor(random() * Math.min(3, gW - 3));
  const start = Math.floor(random() * (gW - width + 1));

  const spans: (Span | null)[] = new Array(gW).fill(null);
  const len = pickLength(gH, gH);
  const lo  = Math.floor(random() * (gH - len + 1));
  spans[start] = { lo, hi: lo + len - 1 };

  for (let c = start + 1; c < start + width; c++) {
    spans[c] = nextSpan(spans[c - 1]!, gH);
  }

  return hasFlatPair(spans, gW, gH) ? null : spans;
}

function spansToCells(spans: (Span | null)[], gW: number): Set<number> {
  const occ = new Set<number>();
  spans.forEach((s, c) => {
    if (s) for (let r = s.lo; r <= s.hi; r++) occ.add(r * gW + c);
  });
  return occ;
}

// ── Crossword generator ───────────────────────────────────────────────────────

export interface CrossSize {
  label: string;
  w: number; h: number;
  cells: [number, number]; // how much of the grid a board should fill
}

/** S / M / L / XL. L is the size cross mode has always been. */
export const CROSS_SIZES: CrossSize[] = [
  { label: 'S',  w: 5, h: 5, cells: [10, 18] },
  { label: 'M',  w: 6, h: 6, cells: [13, 23] },
  { label: 'L',  w: 6, h: 7, cells: [17, 28] },
  { label: 'XL', w: 8, h: 8, cells: [24, 44] },
];

// How many lines a board should end up with, as a share of the columns it has
// to play with. More lines means neighbouring columns, so closer hues — which
// is most of what makes a board harder.
const LINE_SHARE: [number, number][] = [
  [0.50, 0.833], // Easy
  [0.667, 1.00], // Normal
  [0.833, 1.333], // Hard
];

function lineRangeFor(diffIndex: number, cols: number): [number, number] {
  const [lo, hi] = LINE_SHARE[diffIndex];
  return [Math.max(3, Math.round(cols * lo)), Math.max(4, Math.round(cols * hi))];
}

function attempt(
  diffIndex: number, vivid: boolean, sideways: boolean, size: CrossSize,
  cellRange: [number, number], lineRange: (cols: number) => [number, number],
): CrosswordData | null {
  // Build with hue running across the columns, then optionally transpose the
  // finished board so it runs down the screen instead.
  const bw = sideways ? size.h : size.w;
  const bh = sideways ? size.w : size.h;

  const spans = buildShape(bw, bh);
  if (!spans) return null;
  const occ = spansToCells(spans, bw);
  if (occ.size < cellRange[0] || occ.size > cellRange[1]) return null;

  const gridW = sideways ? bh : bw;
  const gridH = sideways ? bw : bh;
  const place = (r: number, c: number) => sideways ? c * gridW + r : r * gridW + c;

  const activeCells = [...occ].map(fp => place(Math.floor(fp / bw), fp % bw))
                              .sort((a, b) => a - b);
  const lines = extractRuns(new Set(activeCells), gridW, gridH);
  const [minLines, maxLines] = lineRange(bw);
  if (lines.length < minLines || lines.length > maxLines) return null;
  // Every cell must sit on a line, or it would have no gradient to belong to.
  const covered = new Set(lines.flatMap(l => l.cells));
  if (activeCells.some(fp => !covered.has(fp))) return null;

  const field = randomCrossField(diffIndex, bh, bw, vivid);
  const color = new Map<number, number>();
  for (const fp of occ) {
    const r = Math.floor(fp / bw), c = fp % bw;
    color.set(place(r, c), fieldHex(field, r, c));
  }

  // Pin the outermost cell on each side of both axes, which is what fixes the
  // two directions. Found in build space so they stay on the right axis when
  // transposed: columns carry hue, rows carry lightness.
  const ends = { minC: bw, maxC: -1, minR: bh, maxR: -1 };
  const pinned = new Map<keyof typeof ends, number>();
  for (const fp of occ) {
    const r = Math.floor(fp / bw), c = fp % bw;
    if (c < ends.minC) { ends.minC = c; pinned.set('minC', place(r, c)); }
    if (c > ends.maxC) { ends.maxC = c; pinned.set('maxC', place(r, c)); }
    if (r < ends.minR) { ends.minR = r; pinned.set('minR', place(r, c)); }
    if (r > ends.maxR) { ends.maxR = r; pinned.set('maxR', place(r, c)); }
  }

  const pins = new Set(pinned.values());

  // A shape needn't reach every edge of the grid it grew in, and the leftover
  // margin would show up as the board sitting off-centre. Trim to what's
  // actually used, which also lets the tiles be drawn that bit larger.
  let minR = gridH, maxR = -1, minC = gridW, maxC = -1;
  for (const fp of activeCells) {
    const r = Math.floor(fp / gridW), c = fp % gridW;
    minR = Math.min(minR, r); maxR = Math.max(maxR, r);
    minC = Math.min(minC, c); maxC = Math.max(maxC, c);
  }
  const cropW = maxC - minC + 1;
  const cropH = maxR - minR + 1;
  const crop  = (fp: number) =>
    (Math.floor(fp / gridW) - minR) * cropW + (fp % gridW - minC);

  // Cropping shifts every cell by the same amount, so the ordering holds.
  const cropped = activeCells.map(crop);
  const anchors = new Set<number>();
  activeCells.forEach((fp, i) => { if (pins.has(fp)) anchors.add(i); });

  return {
    gridW: cropW, gridH: cropH,
    activeCells: cropped,
    colors: activeCells.map(fp => color.get(fp)!),
    anchors,
    lines: lines.map(l => ({ dir: l.dir, cells: l.cells.map(crop) })),
  };
}

export function generateCrossword(
  diffIndex: number, vivid: boolean, sizeIndex = 2,
): CrosswordData {
  const size     = CROSS_SIZES[sizeIndex] ?? CROSS_SIZES[2];
  const sideways = random() < 0.5;
  const anyLines = (): [number, number] => [3, 99];

  // Ask for the difficulty's line count first, then drop that, then let the
  // board be any size it likes. Each step gives up less than falling back to a
  // plain rectangle would.
  const passes: [[number, number], (cols: number) => [number, number]][] = [
    [size.cells, cols => lineRangeFor(diffIndex, cols)],
    [size.cells, anyLines],
    [[6, size.w * size.h], anyLines],
  ];

  for (const [cells, lines] of passes) {
    for (let retry = 0; retry < 600; retry++) {
      const result = attempt(diffIndex, vivid, sideways, size, cells, lines);
      if (result) return result;
    }
  }
  return generateFullGrid(diffIndex, size.h, size.w, vivid);
}
